import React from "react";
import { Link } from "react-router-dom";
import { Fence, ChevronDown, ChevronUp } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const mmdd = (iso) => (iso && iso.length >= 10 ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}` : "—");

export function SiteCostChip({ c, lang, open, onToggle }) {
  const zh = lang === "zh";
  const tone = c.over ? "text-white bg-red-600 border-red-700" : c.near ? "text-amber-800 bg-amber-100 border-amber-300" : "text-slate-600 bg-slate-100 border-slate-200 hover:bg-slate-200";
  const title = zh
    ? `围栏/厕所累计租赁 ${money(c.billsTotal)} · 预估 ${money(c.estimatedTotal)}${c.pct != null ? ` (${c.pct}%)` : ""}${c.missingCount ? ` · ${c.missingCount} 个月账单未登记` : ""}`
    : `Site rental billed ${money(c.billsTotal)} · est. ${money(c.estimatedTotal)}${c.pct != null ? ` (${c.pct}%)` : ""}`;
  return (
    <button type="button" data-testid={`site-cost-chip-${c.projectId}`} data-over={c.over ? "1" : "0"} data-near={c.near ? "1" : "0"} data-missing={c.missingCount || 0} onClick={onToggle} title={title}
      className={`inline-flex items-center gap-0.5 text-[10px] font-semibold rounded-full px-1.5 py-0.5 shrink-0 border transition-colors ${tone}`}>
      <Fence className="h-3 w-3" />
      {zh ? `后勤租赁 ${money(c.billsTotal)}` : `Rental ${money(c.billsTotal)}`}
      {c.over && <span>{zh ? " · 超预估" : " · over est."}</span>}
      {!c.over && c.near && <span>{zh ? ` · ${c.pct}%` : ` · ${c.pct}%`}</span>}
      {c.missingCount > 0 && <span className={c.over ? "" : "text-red-600"}>{zh ? ` · 漏登 ${c.missingCount}` : ` · ${c.missingCount} unbilled`}</span>}
      {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
    </button>
  );
}

export function SiteCostDetailRow({ c, lang, colSpan }) {
  const zh = lang === "zh";
  return (
    <tr data-testid={`site-cost-detail-${c.projectId}`} className="bg-slate-50/80">
      <td colSpan={colSpan} className="px-6 py-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-wrap items-center gap-4 text-[11px] text-slate-600 mb-1.5">
          <span>{zh ? "在租月租合计" : "Monthly rent"} <b className="text-slate-800">{money(c.monthlyRent)}</b></span>
          <span>{zh ? "预估总额" : "Estimated"} <b className="text-slate-800">{money(c.estimatedTotal)}</b></span>
          <span className={c.over ? "text-red-700" : ""}>{zh ? "实际账单累计" : "Billed"} <b>{money(c.billsTotal)}</b>{c.pct != null && ` (${c.pct}%)`}{c.over && (zh ? " · 已超预估" : " · over estimate")}</span>
          <Link to={`/project/${c.projectId}?task=WF-03-01-01`} data-testid={`site-cost-open-${c.projectId}`} className="text-sky-700 hover:underline ml-auto">{zh ? "打开任务链费用登记 →" : "Open task chain →"}</Link>
        </div>
        <table className="w-full text-[11px]">
          <thead><tr className="text-slate-400 text-left"><th className="py-1 pr-3 font-medium">{zh ? "订单" : "Order"}</th><th className="py-1 pr-3 font-medium">{zh ? "月租" : "Rent/mo"}</th><th className="py-1 pr-3 font-medium">{zh ? "租期" : "Rental"}</th><th className="py-1 pr-3 font-medium text-right">{zh ? "预估" : "Est."}</th><th className="py-1 pr-3 font-medium text-right">{zh ? "实际账单" : "Billed"}</th><th className="py-1 font-medium">{zh ? "状态" : "Status"}</th></tr></thead>
          <tbody>
            {c.orders.map((o) => (
              <tr key={o.id} data-testid={`site-cost-order-${o.id}`} data-over={o.over ? "1" : "0"} className={`border-t border-slate-200 ${o.over ? "text-red-700" : "text-slate-700"}`}>
                <td className="py-1 pr-3">{zh ? o.kindZh : o.kind} · {o.summary}</td>
                <td className="py-1 pr-3">{o.monthlyRent ? money(o.monthlyRent) : (zh ? "未登记" : "—")}</td>
                <td className="py-1 pr-3">{mmdd(o.rentalStart)} → {mmdd(o.rentalEnd)}{o.rentalMonths ? `（${o.rentalMonths}${zh ? " 个月" : " mo"}）` : ""}</td>
                <td className="py-1 pr-3 text-right">{o.estimatedTotal != null ? money(o.estimatedTotal) : "—"}</td>
                <td className="py-1 pr-3 text-right font-semibold">{money(o.billsTotal)}<span className="text-slate-400 font-normal"> · {o.billCount} {zh ? "张" : "bill(s)"}</span></td>
                <td className="py-1">
                  {o.removed ? (zh ? "已撤场" : "Removed") : o.over ? (zh ? "超预估" : "Over est.") : (zh ? "在租" : "Active")}
                  {o.missingPeriods?.length > 0 && <span className="ml-1 text-red-600">{zh ? `漏登 ${o.missingPeriods.join("、")}` : `Unbilled ${o.missingPeriods.join(", ")}`}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </td>
    </tr>
  );
}
